import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { getEventSlice } from "../../redux/slices/eventSlice";
import { getAccountSlice } from "../../redux/slices/accountSlice";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import RectangleButton from "../Buttons/RectangleButton";
import EmptyStateButton from "../Buttons/EmptyStateButton";
import { Colors } from "../../utils/colors";
import { useNavigate } from "../../hooks/useNavigate";
import { NavigationId } from "../../navigation/AppParamList";
import { getProductDetails } from "../../utils/productUtils";
import {
  GetDateWithTime,
  isProductRelatedType,
  userRelatedProducts,
} from "@markit/common.utils";
import { Event, EventType } from "@markit/common.types";
import { onProductNavigatePath } from "../../utils/navigationUtils";

const RECENT_PRODUCTS_LIMIT = 3;

// Shows the creator's most recently created products (events, posts, etc).
const RecentProductsSection = () => {
  const { events, tickets } = useSelector(getEventSlice);
  const { accountData } = useSelector(getAccountSlice);
  const navigate = useNavigate();

  const styles = {
    overallContainerView: {
      borderRadius: 20,
      paddingBlock: 14,
      backgroundColor: Colors.WHITE,
      minHeight: 225,
    },
  };

  const recentProducts: Event[] = useMemo(() => {
    const userProducts = userRelatedProducts(
      events,
      accountData.uid,
      tickets
    ).filter(
      (product) =>
        (isProductRelatedType(product.eventType) ||
          product.eventType === EventType.GENERIC_LINK) &&
        !product.isDraft
    );
    return [...userProducts]
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      .slice(0, RECENT_PRODUCTS_LIMIT);
  }, [events, tickets, accountData.uid]);

  return (
    <StandardBorderedContainer
      containerStyles={{
        ...styles.overallContainerView,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <div className="ColumnNormal" style={{ gap: 14 }}>
        <div
          className="AlignedRowSelect"
          style={{ gap: 7, paddingInline: 14 }}
          onClick={() => navigate(NavigationId.HOME_PRODUCTS)}
        >
          <span className="bodyMedium">Recent Products</span>
          <Icon
            icon="ion:chevron-right"
            height={12}
            style={{ color: Colors.BLACK }}
          />
        </div>
        <HorizontalDivider altMargin={0} />
      </div>
      {recentProducts.length === 0 ? (
        <EmptyStateButton
          title="No Products Yet"
          description="Products you create will show up here"
          icon={<Icon icon="ion:bag-handle" height={35} color={Colors.BLACK} />}
          containerStyles={{ marginTop: 36 }}
        />
      ) : (
        <div className="ColumnNormal">
          {recentProducts.map((product, index) => {
            const productDetails = getProductDetails(product.eventType);
            return (
              <div key={product.id}>
                <div
                  className="AlignedRowSpaced"
                  style={{
                    padding: 14,
                    paddingBottom: index === recentProducts.length - 1 ? 0 : 14,
                    gap: 14,
                  }}
                >
                  <div className="AlignedRow" style={{ gap: 14 }}>
                    <Icon
                      icon={productDetails.icon || "ion:bag-handle"}
                      height={24}
                      style={{ color: Colors.BLACK, flexShrink: 0 }}
                    />
                    <div className="ColumnNormal" style={{ gap: 4 }}>
                      <span className="bodyMedium TwoLineText">
                        {product.title}
                      </span>
                      <span
                        className="bodySmallRegular"
                        style={{ color: Colors.GRAY1 }}
                      >
                        {productDetails.singular
                          ? `${productDetails.singular} • `
                          : ""}
                        Created {GetDateWithTime(product.createdAt, true)}
                      </span>
                    </div>
                  </div>
                  <RectangleButton
                    buttonLabel="View"
                    altColor={Colors.WHITE1}
                    altTextColor={Colors.BLACK}
                    altBorderRadius={100}
                    containerStyles={{
                      width: 90,
                      border: `0.5px solid ${Colors.GRAY2}`,
                      flexShrink: 0,
                    }}
                    onPress={() => onProductNavigatePath(navigate, product.id)}
                  />
                </div>
                {index !== recentProducts.length - 1 ? (
                  <HorizontalDivider altMargin={0} />
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </StandardBorderedContainer>
  );
};

export default RecentProductsSection;
